"use client";

import { useState } from "react";
import { useToolLocale, useToolUi } from "@/components/tools/tool-ui";
import { tPlural } from "@/lib/i18n/interpolate";
import { parseIntegerInput } from "@/lib/parse-number";

type StyleKey =
  | "apa7"
  | "mla9"
  | "chicago-ad"
  | "chicago-notes"
  | "harvard"
  | "vancouver"
  | "ieee";

interface StyleInfo {
  getInText: (count: number, names: string[], strings: Strings) => string;
  getReference: (count: number, names: string[]) => string;
}

const PLACEHOLDER_NAMES = [
  "Smith",
  "Johnson",
  "Williams",
  "Brown",
  "Jones",
  "Garcia",
  "Miller",
  "Davis",
  "Rodriguez",
  "Martinez",
  "Hernandez",
  "Lopez",
  "Gonzalez",
  "Wilson",
  "Anderson",
  "Thomas",
  "Taylor",
  "Moore",
  "Jackson",
  "Martin",
  "White",
  "Harris",
  "Clark",
  "Lewis",
  "Walker",
];

/** Enough placeholder surnames to show APA 7's 21-or-more form, which needs a 21st author. */
const MAX_AUTHORS = PLACEHOLDER_NAMES.length;

function getNames(count: number): string[] {
  return PLACEHOLDER_NAMES.slice(0, Math.min(count, PLACEHOLDER_NAMES.length));
}

function joinNames(names: string[], conjunction: string): string {
  if (names.length === 1) {
    return names[0];
  }
  if (names.length === 2) {
    return `${names[0]} ${conjunction} ${names[1]}`;
  }
  return `${names.slice(0, -1).join(", ")}, ${conjunction} ${names.at(-1)}`;
}

/**
 * The author slot of a Chicago bibliography or reference-list entry, with its closing
 * period: only the first name is inverted, and seven or more authors become the first
 * three followed by et al.
 */
function chicagoBibliographyNames(count: number, names: string[]): string {
  const truncated = count > 6;
  const listed = names
    .slice(0, truncated ? 3 : count)
    .map((name, index) => (index === 0 ? `${name}, Xavier` : `Xavier ${name}`));
  if (truncated) {
    return `${listed.join(", ")}, et al.`;
  }
  if (listed.length === 1) {
    return `${listed[0]}.`;
  }
  return `${listed.slice(0, -1).join(", ")}, and ${listed.at(-1)}.`;
}

const STYLES: Record<StyleKey, StyleInfo> = {
  apa7: {
    getInText(count, names) {
      if (count === 1) {
        return `(${names[0]}, 2024)`;
      }
      if (count === 2) {
        return `(${names[0]} & ${names[1]}, 2024)`;
      }
      return `(${names[0]} et al., 2024)`;
    },
    getReference(count, names) {
      if (count <= 20) {
        const allNames = names.map((n) => `${n}, X.`);
        const last = allNames.pop();
        if (allNames.length === 0) {
          return `${last} (2024). Title of work.`;
        }
        return `${allNames.join(", ")}, & ${last} (2024). Title of work.`;
      }
      const first19 = names
        .slice(0, 19)
        .map((n) => `${n}, X.`)
        .join(", ");
      return `${first19}, ... ${names.at(-1)}, X. (2024). Title of work.`;
    },
  },
  mla9: {
    getInText(count, names) {
      if (count === 1) {
        return `(${names[0]} 15)`;
      }
      if (count === 2) {
        return `(${names[0]} and ${names[1]} 15)`;
      }
      return `(${names[0]} et al. 15)`;
    },
    getReference(count, names) {
      if (count === 1) {
        return `${names[0]}, Xavier. Title of Work. Publisher, 2024.`;
      }
      if (count === 2) {
        // MLA 9: only the first author is inverted — "Last, First, and First Last."
        return `${names[0]}, Xavier, and Yolanda ${names[1]}. Title of Work. Publisher, 2024.`;
      }
      return `${names[0]}, Xavier, et al. Title of Work. Publisher, 2024.`;
    },
  },
  // Chicago 18th edition (CMOS 13.23, 13.107): notes and author-date citations
  // name one or two authors and switch to et al. at three; the bibliography or
  // reference list names up to six and gives the first three plus et al. for
  // seven or more.
  "chicago-ad": {
    getInText(count, names) {
      if (count === 1) {
        return `(${names[0]} 2024, 15)`;
      }
      if (count === 2) {
        return `(${names[0]} and ${names[1]} 2024, 15)`;
      }
      return `(${names[0]} et al. 2024, 15)`;
    },
    getReference(count, names) {
      return `${chicagoBibliographyNames(count, names)} 2024. Title of Work. Publisher.`;
    },
  },
  "chicago-notes": {
    getInText(count, names) {
      if (count === 1) {
        return `1. Xavier ${names[0]}, Title of Work (Publisher, 2024), 15.`;
      }
      if (count === 2) {
        return `1. Xavier ${names[0]} and Yolanda ${names[1]}, Title of Work (Publisher, 2024), 15.`;
      }
      return `1. Xavier ${names[0]} et al., Title of Work (Publisher, 2024), 15.`;
    },
    getReference(count, names) {
      return `${chicagoBibliographyNames(count, names)} Title of Work. Publisher, 2024.`;
    },
  },
  harvard: {
    getInText(count, names) {
      if (count === 1) {
        return `(${names[0]}, 2024, p. 15)`;
      }
      if (count === 2) {
        return `(${names[0]} and ${names[1]}, 2024, p. 15)`;
      }
      if (count === 3) {
        return `(${names[0]}, ${names[1]} and ${names[2]}, 2024, p. 15)`;
      }
      return `(${names[0]} et al., 2024, p. 15)`;
    },
    getReference(_count, names) {
      const allNames = names.map((n) => `${n}, X.`);
      const last = allNames.pop();
      if (allNames.length === 0) {
        return `${last} (2024) Title of work. Publisher.`;
      }
      return `${allNames.join(", ")} and ${last} (2024) Title of work. Publisher.`;
    },
  },
  vancouver: {
    getInText(_count, _names, strings) {
      return strings.vancouverInTextExample;
    },
    getReference(count, names) {
      if (count <= 6) {
        return `${joinNames(
          names.map((n) => `${n} X`),
          "and"
        )}. Title of work. Publisher; 2024.`;
      }
      const first6 = names
        .slice(0, 6)
        .map((n) => `${n} X`)
        .join(", ");
      return `${first6}, et al. Title of work. Publisher; 2024.`;
    },
  },
  ieee: {
    getInText(_count, _names, strings) {
      return strings.ieeeInTextExample;
    },
    getReference(count, names) {
      if (count <= 6) {
        const listed = names.map((n) => `X. ${n}`);
        return `[1] ${joinNames(listed, "and")}, "Title of work," Publisher, 2024.`;
      }
      return `[1] X. ${names[0]} et al., "Title of work," Publisher, 2024.`;
    },
  },
};

const STYLE_KEYS: StyleKey[] = [
  "apa7",
  "mla9",
  "chicago-ad",
  "chicago-notes",
  "harvard",
  "vancouver",
  "ieee",
];

/** English strings. A translated content file overrides these through `ui`. */
export const ET_AL_CALCULATOR_STRINGS = {
  numberOfAuthors: "Number of authors",
  citationStyle: "Citation style",
  rulesHeading:
    "one:{style} Rules for {count} Author|other:{style} Rules for {count} Authors",
  inTextCitationRule: "In-text citation rule",
  referenceListRule: "Reference list rule",
  exampleHeading:
    "one:Example with {count} author|other:Example with {count} authors",
  inTextCitation: "In-text citation",
  referenceListEntry: "Reference list entry",
  quickReferenceWhenToUse: "Quick Reference: When to Use Et Al.",
  style: "Style",
  inText: "In-Text",
  referenceList: "Reference List",
  apa7th: "APA 7th",
  n3Authors: "3+ authors",
  n21Authors: "21+ authors",
  mla9th: "MLA 9th",
  chicagoAD: "Chicago A-D",
  n4Authors: "4+ authors",
  chicagoNotes: "Chicago Notes",
  harvard: "Harvard",
  listAll: "List all",
  vancouver: "Vancouver",
  numbersOnly: "Numbers only",
  n7Authors: "7+ authors",
  ieee: "IEEE",
  styleLabels: {
    apa7: "APA 7th Edition",
    mla9: "MLA 9th Edition",
    "chicago-ad": "Chicago Author-Date",
    "chicago-notes": "Chicago Notes-Bibliography",
    harvard: "Harvard",
    vancouver: "Vancouver",
    ieee: "IEEE",
  },
  inTextRules: {
    apa7: "1-2 authors: list all. 3+ authors: first author et al.",
    mla9: "1-2 authors: list all. 3+ authors: first author et al.",
    "chicago-ad": "1-2 authors: list all. 3+ authors: first author et al.",
    "chicago-notes":
      "1-2 authors: list all in first note. 3+: first author et al. Subsequent notes: shortened form.",
    harvard: "1-3 authors: list all. 4+ authors: first author et al.",
    vancouver: "Use superscript numbers in text (no author names in-text).",
    ieee: "Use bracketed numbers in text (no author names in-text).",
  },
  referenceRules: {
    apa7: "List up to 20 authors. For 21+, list first 19, ellipsis, then last author.",
    mla9: "1 author: list full name. 2 authors: list both. 3+: first author et al.",
    "chicago-ad":
      "List up to 6 authors. For 7+, list first 3 followed by et al.",
    "chicago-notes":
      "List up to 6 authors. For 7+, list first 3 followed by et al.",
    harvard: "List all authors in the reference list regardless of count.",
    vancouver:
      "1-6 authors: list all. 7+ authors: list first 6 followed by et al.",
    ieee: "1-6 authors: list all. 7+ authors: first author et al.",
  },
  vancouverInTextExample: "Text of the sentence.1",
  ieeeInTextExample: "Text of the sentence [1].",
} as const;

type Strings = typeof ET_AL_CALCULATOR_STRINGS;

/** The in-text and reference-list examples the widget shows for a style and author count. */
export function etAlExamples(
  style: StyleKey,
  count: number,
  strings: Strings = ET_AL_CALCULATOR_STRINGS
): { inText: string; reference: string } {
  const names = getNames(count);
  return {
    inText: STYLES[style].getInText(count, names, strings),
    reference: STYLES[style].getReference(count, names),
  };
}

export default function EtAlCalculatorWidget() {
  const [authorCount, setAuthorCount] = useState(3);
  const [style, setStyle] = useState<StyleKey>("apa7");
  const strings = useToolUi(ET_AL_CALCULATOR_STRINGS);
  const locale = useToolLocale();

  const { inText: inTextExample, reference: refExample } = etAlExamples(
    style,
    authorCount,
    strings
  );

  function highlightEtAl(text: string): React.ReactNode {
    const parts = text.split(/(et al\.?)/g);
    return parts.map((part, i) => {
      if (part === "et al." || part === "et al") {
        return (
          <span
            className="rounded bg-amber-100 px-1 font-semibold text-amber-800 dark:bg-amber-900/30 dark:text-amber-300"
            key={`etal-${
              // biome-ignore lint/suspicious/noArrayIndexKey: stable list
              i
            }`}
          >
            {part}
          </span>
        );
      }
      return (
        <span
          key={`text-${
            // biome-ignore lint/suspicious/noArrayIndexKey: stable list
            i
          }`}
        >
          {part}
        </span>
      );
    });
  }

  return (
    <>
      {/* Controls */}
      <div className="flex flex-wrap items-end gap-6 rounded-lg border border-border bg-muted/30 p-5">
        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="author-count"
          >
            {strings.numberOfAuthors}
          </label>
          <input
            className="w-24 rounded-lg border border-border bg-card px-3 py-2 text-center text-foreground text-sm focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
            id="author-count"
            max={MAX_AUTHORS}
            min={1}
            onChange={(e) => {
              const val = parseIntegerInput(e.target.value);
              if (val !== null && val >= 1 && val <= MAX_AUTHORS) {
                setAuthorCount(val);
              }
            }}
            type="number"
            value={authorCount}
          />
        </div>

        <div className="space-y-2">
          <label
            className="block font-medium text-foreground text-sm"
            htmlFor="style-select"
          >
            {strings.citationStyle}
          </label>
          <select
            className="rounded-lg border border-border bg-card px-3 py-2 text-foreground text-sm focus:border-foreground/30 focus:outline-none focus:ring-1 focus:ring-foreground/30"
            id="style-select"
            onChange={(e) => setStyle(e.target.value as StyleKey)}
            value={style}
          >
            {STYLE_KEYS.map((key) => (
              <option key={key} value={key}>
                {strings.styleLabels[key]}
              </option>
            ))}
          </select>
        </div>
      </div>
      {/* Rules */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {tPlural(strings.rulesHeading, locale, authorCount, {
            style: strings.styleLabels[style],
          })}
        </h2>

        <div className="space-y-3">
          <div className="rounded-lg bg-muted/30 p-4">
            <h3 className="mb-1 font-medium text-foreground text-sm">
              {strings.inTextCitationRule}
            </h3>
            <p className="text-muted-foreground text-sm">
              {strings.inTextRules[style]}
            </p>
          </div>

          <div className="rounded-lg bg-muted/30 p-4">
            <h3 className="mb-1 font-medium text-foreground text-sm">
              {strings.referenceListRule}
            </h3>
            <p className="text-muted-foreground text-sm">
              {strings.referenceRules[style]}
            </p>
          </div>
        </div>
      </div>
      {/* Examples */}
      <div className="space-y-4 rounded-lg border border-border bg-card p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {tPlural(strings.exampleHeading, locale, authorCount)}
        </h2>

        <div className="space-y-3">
          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-2 font-medium text-foreground text-sm">
              {strings.inTextCitation}
            </h3>
            <p className="font-mono text-foreground text-sm leading-relaxed">
              {highlightEtAl(inTextExample)}
            </p>
          </div>

          <div className="rounded-lg border border-border bg-muted/30 p-4">
            <h3 className="mb-2 font-medium text-foreground text-sm">
              {strings.referenceListEntry}
            </h3>
            <p className="font-mono text-foreground text-sm leading-relaxed">
              {highlightEtAl(refExample)}
            </p>
          </div>
        </div>
      </div>
      {/* Quick reference table */}
      <section className="space-y-3 rounded-lg border border-border bg-muted/30 p-6">
        <h2 className="font-semibold text-foreground text-lg">
          {strings.quickReferenceWhenToUse}
        </h2>
        <div className="overflow-x-auto">
          <table className="w-full text-left text-sm">
            <thead>
              <tr className="border-border border-b">
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.style}
                </th>
                <th className="py-2 pr-4 font-medium text-foreground">
                  {strings.inText}
                </th>
                <th className="py-2 font-medium text-foreground">
                  {strings.referenceList}
                </th>
              </tr>
            </thead>
            <tbody className="text-muted-foreground">
              <tr className="border-border/50 border-b">
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.apa7th}
                </td>
                <td className="py-2 pr-4">{strings.n3Authors}</td>
                <td className="py-2">{strings.n21Authors}</td>
              </tr>
              <tr className="border-border/50 border-b">
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.mla9th}
                </td>
                <td className="py-2 pr-4">{strings.n3Authors}</td>
                <td className="py-2">{strings.n3Authors}</td>
              </tr>
              <tr className="border-border/50 border-b">
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.chicagoAD}
                </td>
                <td className="py-2 pr-4">{strings.n3Authors}</td>
                <td className="py-2">{strings.n7Authors}</td>
              </tr>
              <tr className="border-border/50 border-b">
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.chicagoNotes}
                </td>
                <td className="py-2 pr-4">{strings.n3Authors}</td>
                <td className="py-2">{strings.n7Authors}</td>
              </tr>
              <tr className="border-border/50 border-b">
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.harvard}
                </td>
                <td className="py-2 pr-4">{strings.n4Authors}</td>
                <td className="py-2">{strings.listAll}</td>
              </tr>
              <tr className="border-border/50 border-b">
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.vancouver}
                </td>
                <td className="py-2 pr-4">{strings.numbersOnly}</td>
                <td className="py-2">{strings.n7Authors}</td>
              </tr>
              <tr>
                <td className="py-2 pr-4 font-medium text-foreground">
                  {strings.ieee}
                </td>
                <td className="py-2 pr-4">{strings.numbersOnly}</td>
                <td className="py-2">{strings.n7Authors}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </section>
      {/* Related tools */}
    </>
  );
}
